// l2b-engine.js — Language-to-Body training engine.
//
// PORT TARGET:
//   Assets/KFE/Scripts/Runtime/Exercises/L2B/L2BEngine.cs
//   Assets/KFE/Scripts/Runtime/Exercises/L2B/L2BModels.cs
//
// Pure state machine. No DOM imports, no fetch, no audio. Mirrors the C#
// engine's public API one-for-one: method names, field ordering, even
// internal helper names map back so `git diff` against the Unity port
// stays readable.
//
// Public API (matches L2BEngine.cs):
//   new L2BEngine(items, config)     → engine instance
//   start()                          → returns first L2BState
//   onReplayPressed()                → returns current L2BState (no change)
//   onAdvancePressed()               → exit large intro, advance pointer
//   onTileSelected(conceptId)        → answer; returns Feedback state
//   onFeedbackComplete()             → returns next state after feedback delay
//   snapshot()                       → current L2BState
//
// Events (CustomEvent on the engine, mirrors C# OnFoo events):
//   "stateChanged" — fired any time state advances; detail = snapshot
//   "result"       — { correct, conceptId } after onTileSelected
//   "completed"    — fired when state.mode === "complete"
//
// Architecture rule (CLAUDE.md): engines never reference UI; screens consume
// engines via state snapshots only. Keep this file pure.

/** @typedef {import("../data/mmu-loader.js").Mmu} Mmu */

// ─────────────────────────────────────────────────────────────
// Models — mirror L2BModels.cs
// ─────────────────────────────────────────────────────────────

/**
 * @typedef {object} L2BItemData
 * @property {string} conceptId               unique id (string form of mmuId)
 * @property {string} imageFile               legacy image filename
 * @property {string} audioFile               legacy audio filename
 * @property {string} displayText             phrase shown for TextOnly modality
 * @property {"large"|"small"} tag            full-screen intro vs grid-only
 * @property {number} wordCount
 * @property {number} masteryStreakOverride   per-(item, MMU) mastery target
 *           (Brief 101 `itemMasteryTarget`, sourced from the L2B plist's
 *           per-Items RequiredPlay / RequiredMMUPlay via mmu-loader); 0 = use
 *           the engine's defaultMasteryStreak.
 * @property {Mmu}    mmu                     original MMU for media resolution
 */

/**
 * @typedef {object} L2BConfig
 * @property {number} [defaultMasteryStreak=3]
 * @property {number} [maxGridSize=8]
 * @property {number} [autoAdvanceDelay=1.5]
 * @property {"sequential"|"streakGate"|"manual"} [pacing="sequential"]
 * @property {number} [streakGateThreshold=1]
 * @property {"audioOnly"|"textOnly"|"audioAndText"} [modality="audioOnly"]
 */

/**
 * @typedef {"largeIntro"|"tileGrid"|"feedback"|"complete"} PresentationMode
 */

/**
 * @typedef {object} L2BState
 * @property {PresentationMode} mode
 * @property {L2BItemData|null} introItem
 * @property {L2BItemData|null} targetItem
 * @property {L2BItemData[]}    tiles
 * @property {boolean}          wasCorrect
 * @property {string}           correctConceptId
 * @property {string}           selectedConceptId
 * @property {number}           totalItems
 * @property {number}           masteredCount
 * @property {number}           roundsPlayed
 * @property {number}           correctAnswers
 * @property {number}           accuracyPercent
 */

/**
 * Map the Mmu records the screen loaded into the engine's L2BItemData.
 *
 * Decides which MMUs debut full-screen (`tag: "large"` → `largeIntro`) rather
 * than in the tile grid. The AUTHORED per-(item, MMU) `ImageSize` tag wins
 * (Brief 189): the course designer marks conceptual novelty in the L2B
 * container plist, `mmu-loader.getMmusForItem` stamps it onto `isLargeImage`
 * as true / false / undefined, and this line honours it — CLAUDE.md Rule #8,
 * code honours pedagogy signals, it does not invent them.
 *
 * The Unity port's "≤2 words → large intro" rule (L2BExerciseScreen.cs line
 * 311) survives ONLY as the untagged fallback, for content authored after the
 * 2022 corpus that carries no `ImageSize` yet. Measured against the 2022
 * corpus the heuristic diverges from the designer on 488 of 9,432 pairs
 * (5.2%) — it misses multi-word single concepts ("The rubbish bin", "On Top
 * of") and fires spuriously on manner variants of known verbs ("Row fast",
 * "Jump twice"). Do not promote it back to primary; see
 * docs/CMS-CONTRACT-FOR-BRYAN.md §3a.
 *
 * @param {Mmu[]} mmus
 * @returns {L2BItemData[]}
 */
export function mmusToItemData(mmus) {
  return mmus.map((m) => {
    const wc = m.phrase.trim().split(/\s+/).filter(Boolean).length;
    const isLarge = m.isLargeImage ?? wc <= 2; // authored tag wins; heuristic only when untagged
    return {
      conceptId: String(m.mmuId),
      imageFile: m.imageFileName,
      audioFile: m.audioFileName,
      displayText: m.phrase,
      tag: isLarge ? "large" : "small",
      wordCount: wc,
      // Per-(item, MMU) mastery target (Brief 101). getMmusForItem stamps the
      // per-item `itemMasteryTarget` onto m.masteryTarget; the L2BItemRuntime
      // constructor turns a positive override into the required streak, else
      // falls back to the engine's defaultMasteryStreak.
      masteryStreakOverride: m.masteryTarget,
      mmu: m,
    };
  });
}

/**
 * @param {Mmu[]} mmus
 * @returns {Mmu[]}
 */
export function applyL2bDefensiveFilters(mmus) {
  // Tech Report §3.3: drop items not flagged is_l2b, then drop .jpg/.jpeg
  // (those are HAAI photos that may have leaked through).
  return mmus.filter((m) => {
    if (!m.isL2b) return false;
    const ext = (m.imageFileName || "").toLowerCase();
    if (ext.endsWith(".jpg") || ext.endsWith(".jpeg")) return false;
    return true;
  });
}

// ─────────────────────────────────────────────────────────────
// Runtime state per item — mirror L2BItemRuntime
// ─────────────────────────────────────────────────────────────

/**
 * @typedef {"unmet"|"active"|"mastered"} ItemPool
 */

class L2BItemRuntime {
  /**
   * @param {L2BItemData} data
   * @param {number} defaultStreak
   */
  constructor(data, defaultStreak) {
    this.data = data;
    /** @type {ItemPool} */
    this.pool = "unmet";
    this.consecutiveCorrect = 0;
    this.hasBeenIntroducedAsLarge = false;
    this.requiredStreak = data.masteryStreakOverride > 0
      ? data.masteryStreakOverride
      : defaultStreak;
  }
}

// ─────────────────────────────────────────────────────────────
// Engine — mirror L2BEngine.cs
// ─────────────────────────────────────────────────────────────

export class L2BEngine extends EventTarget {
  /**
   * @param {L2BItemData[]} itemData
   * @param {L2BConfig} [config]
   */
  constructor(itemData, config = {}) {
    super();
    /** @type {Required<L2BConfig>} */
    this._config = {
      defaultMasteryStreak: config.defaultMasteryStreak ?? 3,
      maxGridSize:          config.maxGridSize ?? 8,
      autoAdvanceDelay:     config.autoAdvanceDelay ?? 1.5,
      pacing:               config.pacing ?? "sequential",
      streakGateThreshold:  config.streakGateThreshold ?? 1,
      modality:             config.modality ?? "audioOnly",
    };

    /** @type {L2BItemRuntime[]} */
    this._items = itemData.map((d) => new L2BItemRuntime(d, this._config.defaultMasteryStreak));
    /** @type {L2BItemRuntime[]} */
    this._metUniverse = [];
    this._sequencePointer = 0;
    /** @type {string|null} */ this._lastTargetId = null;
    /** @type {L2BItemRuntime|null} */ this._currentTarget = null;
    this._roundsPlayed = 0;
    this._correctAnswers = 0;
    this._awaitingFeedbackAdvance = false;
    /** @type {L2BState|null} */ this._lastFeedbackState = null;
    /** @type {L2BItemRuntime|null} */ this._lastIntroducedItem = null;

    /** @type {L2BState|null} */ this._lastState = null;
  }

  // ─── Public API — called by the presentation layer ────────

  start() {
    return this._publish(this._advanceSequence());
  }

  onReplayPressed() {
    if (this._sequencePointer > 0) {
      const item = this._items[this._sequencePointer - 1];
      // No state change — return the current presentation. If the last
      // advance moved us past the large intro, return the current tile grid.
      if (this._currentTarget == null) return this._publish(this._buildLargeIntroState(item));
    }
    return this._publish(this._lastState ?? this._buildCompleteState());
  }

  onAdvancePressed() {
    // After a largeIntro, the next state must be a tileGrid round testing the
    // just-introduced item. Calling _advanceSequence here would consume the
    // NEXT sequence item — and if that's also large+unintroduced, return
    // another largeIntro. Consecutive large items at the tail of the sequence
    // would then chain into a streak of intros with no practice rounds in
    // between (the Stage 2 Lesson 6 regression).
    if (this._lastState?.mode === "largeIntro" && this._lastIntroducedItem) {
      this._currentTarget = this._lastIntroducedItem;
      this._lastTargetId = this._currentTarget.data.conceptId;
      return this._publish(this._buildTileGridState());
    }
    return this._publish(this._advanceSequence());
  }

  /** @param {string} selectedConceptId */
  onTileSelected(selectedConceptId) {
    if (this._currentTarget == null) return this._publish(this._buildCompleteState());

    this._roundsPlayed++;
    const correct = selectedConceptId === this._currentTarget.data.conceptId;

    if (correct) {
      this._correctAnswers++;
      this._currentTarget.consecutiveCorrect++;
      if (this._currentTarget.consecutiveCorrect >= this._currentTarget.requiredStreak) {
        this._currentTarget.pool = "mastered";
      }
    } else {
      this._currentTarget.consecutiveCorrect = 0;
    }

    this.dispatchEvent(new CustomEvent("result", {
      detail: { correct, conceptId: this._currentTarget.data.conceptId },
    }));

    const fb = this._buildFeedbackState(correct, this._currentTarget.data.conceptId, selectedConceptId);
    this._awaitingFeedbackAdvance = true;
    this._lastFeedbackState = fb;
    return this._publish(fb);
  }

  onFeedbackComplete() {
    this._awaitingFeedbackAdvance = false;
    if (this._isExerciseComplete()) return this._publish(this._buildCompleteState());
    if (this._shouldAdvanceSequence()) return this._publish(this._advanceSequence());
    return this._publish(this._selectTargetAndBuildGrid());
  }

  /** @returns {L2BState} */
  snapshot() {
    return this._lastState ?? this._buildCompleteState();
  }

  // ─── Core algorithm — mirror L2BEngine.cs internals ───────

  _advanceSequence() {
    if (this._sequencePointer >= this._items.length) {
      if (this._isExerciseComplete()) return this._buildCompleteState();
      return this._selectTargetAndBuildGrid();
    }

    const item = this._items[this._sequencePointer];
    this._sequencePointer++;

    if (item.data.tag === "large" && !item.hasBeenIntroducedAsLarge) {
      item.hasBeenIntroducedAsLarge = true;
      item.pool = "active";
      item.consecutiveCorrect = 0;
      this._metUniverse.push(item);
      this._lastIntroducedItem = item;
      return this._buildLargeIntroState(item);
    }

    if (item.pool === "unmet") {
      item.pool = "active";
      item.consecutiveCorrect = 0;
      this._metUniverse.push(item);
      this._lastIntroducedItem = item;
    }

    return this._selectTargetAndBuildGrid();
  }

  _shouldAdvanceSequence() {
    if (this._sequencePointer >= this._items.length) return false;

    const activeItems = this._getActiveItems();
    if (activeItems.length === 0 && this._sequencePointer < this._items.length) return true;

    switch (this._config.pacing) {
      case "sequential": return true;
      case "streakGate":
        if (this._lastIntroducedItem == null) return true;
        return this._lastIntroducedItem.consecutiveCorrect >= this._config.streakGateThreshold
            || this._lastIntroducedItem.pool === "mastered";
      case "manual": return true;
      default: return true;
    }
  }

  _selectTargetAndBuildGrid() {
    const target = this._selectTarget();
    if (target == null) {
      if (this._sequencePointer < this._items.length) return this._advanceSequence();
      return this._buildCompleteState();
    }
    this._currentTarget = target;
    return this._buildTileGridState();
  }

  /** @returns {L2BItemRuntime|null} */
  _selectTarget() {
    let candidates = this._getActiveItems();
    if (candidates.length === 0) return null;

    if (this._lastTargetId != null && candidates.length > 1) {
      candidates = candidates.filter((c) => c.data.conceptId !== this._lastTargetId);
    }
    if (candidates.length === 0) return null;

    let minStreak = Number.POSITIVE_INFINITY;
    for (const c of candidates) if (c.consecutiveCorrect < minStreak) minStreak = c.consecutiveCorrect;
    const lowestStreak = candidates.filter((c) => c.consecutiveCorrect === minStreak);

    const selected = lowestStreak[Math.floor(Math.random() * lowestStreak.length)];
    this._lastTargetId = selected.data.conceptId;
    return selected;
  }

  /**
   * @param {L2BItemRuntime} target
   * @returns {L2BItemData[]}
   */
  _buildTileList(target) {
    /** @type {L2BItemRuntime[]} */ const tiles = [];

    if (this._metUniverse.length <= this._config.maxGridSize) {
      tiles.push(...this._metUniverse);
    } else {
      tiles.push(target);
      let slotsRemaining = this._config.maxGridSize - 1;
      const pool = this._metUniverse.filter((m) => m.data.conceptId !== target.data.conceptId);
      const activeDistractors = pool.filter((m) => m.pool === "active");
      const masteredDistractors = pool.filter((m) => m.pool === "mastered");
      L2BEngine._shuffle(activeDistractors);
      L2BEngine._shuffle(masteredDistractors);
      for (const it of activeDistractors) { if (slotsRemaining <= 0) break; tiles.push(it); slotsRemaining--; }
      for (const it of masteredDistractors) { if (slotsRemaining <= 0) break; tiles.push(it); slotsRemaining--; }
    }

    L2BEngine._shuffle(tiles);
    return tiles.map((t) => t.data);
  }

  // ─── State builders ───────────────────────────────────────

  /** @param {L2BItemRuntime} item @returns {L2BState} */
  _buildLargeIntroState(item) {
    return {
      mode: "largeIntro",
      introItem: item.data,
      targetItem: null,
      tiles: [],
      wasCorrect: false,
      correctConceptId: "",
      selectedConceptId: "",
      totalItems: this._items.length,
      masteredCount: this._countMastered(),
      roundsPlayed: this._roundsPlayed,
      correctAnswers: this._correctAnswers,
      accuracyPercent: this._accuracy(),
    };
  }

  /** @returns {L2BState} */
  _buildTileGridState() {
    const target = this._currentTarget;
    if (target == null) return this._buildCompleteState();
    return {
      mode: "tileGrid",
      introItem: null,
      targetItem: target.data,
      tiles: this._buildTileList(target),
      wasCorrect: false,
      correctConceptId: "",
      selectedConceptId: "",
      totalItems: this._items.length,
      masteredCount: this._countMastered(),
      roundsPlayed: this._roundsPlayed,
      correctAnswers: this._correctAnswers,
      accuracyPercent: this._accuracy(),
    };
  }

  /** @param {boolean} correct @param {string} correctId @param {string} selectedId @returns {L2BState} */
  _buildFeedbackState(correct, correctId, selectedId) {
    const target = this._currentTarget;
    return {
      mode: "feedback",
      introItem: null,
      targetItem: target ? target.data : null,
      tiles: target ? this._buildTileList(target) : [],
      wasCorrect: correct,
      correctConceptId: correctId,
      selectedConceptId: selectedId,
      totalItems: this._items.length,
      masteredCount: this._countMastered(),
      roundsPlayed: this._roundsPlayed,
      correctAnswers: this._correctAnswers,
      accuracyPercent: this._accuracy(),
    };
  }

  /** @returns {L2BState} */
  _buildCompleteState() {
    return {
      mode: "complete",
      introItem: null,
      targetItem: null,
      tiles: [],
      wasCorrect: false,
      correctConceptId: "",
      selectedConceptId: "",
      totalItems: this._items.length,
      masteredCount: this._countMastered(),
      roundsPlayed: this._roundsPlayed,
      correctAnswers: this._correctAnswers,
      accuracyPercent: this._accuracy(),
    };
  }

  // ─── Helpers ──────────────────────────────────────────────

  _getActiveItems() {
    return this._metUniverse.filter((m) => m.pool === "active");
  }

  _countMastered() {
    let n = 0;
    for (const m of this._metUniverse) if (m.pool === "mastered") n++;
    return n;
  }

  _isExerciseComplete() {
    return this._sequencePointer >= this._items.length
        && this._metUniverse.every((m) => m.pool === "mastered");
  }

  _accuracy() {
    return this._roundsPlayed > 0
      ? (this._correctAnswers / this._roundsPlayed) * 100
      : 0;
  }

  /**
   * @template T
   * @param {T[]} list
   */
  static _shuffle(list) {
    for (let i = list.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const tmp = list[i]; list[i] = list[j]; list[j] = tmp;
    }
  }

  /**
   * Save the produced state, dispatch stateChanged, and surface completion.
   * @param {L2BState} state
   * @returns {L2BState}
   */
  _publish(state) {
    this._lastState = state;
    this.dispatchEvent(new CustomEvent("stateChanged", { detail: state }));
    if (state.mode === "complete") this.dispatchEvent(new Event("completed"));
    return state;
  }
}
